/**
 * People Cockpit API service.
 *
 * Calls the proxy's restricted-DWH endpoint. The proxy authorizes
 * `space.people.people_cockpit.view` server-side with `alwaysEnforce`, so a caller
 * without it gets a 403 regardless of the UI.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export type PeopleCockpitRow = Record<string, string | number | boolean | null>;

export interface PeopleCockpitData {
  main: PeopleCockpitRow[] | null;
  forward: PeopleCockpitRow[] | null;
  probation: PeopleCockpitRow[] | null;
  errors: Partial<Record<'main' | 'forward' | 'probation', string>>;
  generatedAt?: string;
  project?: string;
}

/** Thrown for a 403 so the view can show "access restricted" instead of an error. */
export class PeopleCockpitForbiddenError extends Error {
  constructor() {
    super('Forbidden');
    this.name = 'PeopleCockpitForbiddenError';
  }
}

export async function fetchPeopleCockpitData(fresh = false): Promise<PeopleCockpitData> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/people-cockpit/data${fresh ? '?fresh=1' : ''}`);
  if (res.status === 403) throw new PeopleCockpitForbiddenError();
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.errors?.main || body?.error || '';
    } catch {
      // non-JSON error body
    }
    throw new Error(`People Cockpit fetch failed: ${res.status}${detail ? ` — ${detail}` : ''}`);
  }
  return res.json();
}
